import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

// The trailer lives on a real WebGL surface (VideoTexture on a plane) so the
// seats, walls and props of the soundstage occlude it like any other geometry.
// Its soundtrack is routed through a WebAudio panner placed on the screen, so
// it is only audible — and directional — inside the auditorium.

const FWD = new THREE.Vector3();
const SCREEN_POS = [44.38, 3, -4];
// source is 1920x960 (2:1) — plane keeps that ratio inside the 11.2 m frame
const SCREEN_W = 9.2;
const SCREEN_H = 4.6;
const SRC = "/media/cinema-reel.mp4";
const SRC_WEBM = "/media/cinema-reel.webm";

// One single projection for the whole session. A media element can be bound to
// exactly one MediaElementAudioSourceNode: recreating it on every mount (React
// strict mode, route changes) is what makes two soundtracks overlap.
let REEL = null;

function getReel() {
  if (REEL) return REEL;
  const video = document.createElement("video");
  // VP9/webm first (universally decodable), H.264 as the Safari fallback
  const webm = document.createElement("source");
  webm.src = SRC_WEBM;
  webm.type = "video/webm";
  const mp4 = document.createElement("source");
  mp4.src = SRC;
  mp4.type = "video/mp4";
  video.appendChild(webm);
  video.appendChild(mp4);
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.preload = "auto";
  video.crossOrigin = "anonymous";
  video.volume = 0;

  const texture = new THREE.VideoTexture(video);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  const probe = document.createElement("canvas");
  probe.width = 8;
  probe.height = 8;

  REEL = { video, texture, gestured: false, probeCtx: probe.getContext("2d", { willReadFrequently: true }), audio: null };
  window.__cinemaVideo = video;
  return REEL;
}

function ensureAudio(reel) {
  if (reel.audio) return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  const ctx = new Ctx();
  const source = ctx.createMediaElementSource(reel.video);
  const panner = ctx.createPanner();
  panner.panningModel = "HRTF";
  panner.distanceModel = "inverse";
  panner.refDistance = 8;
  panner.maxDistance = 60;
  panner.rolloffFactor = 0.5;
  panner.positionX.value = SCREEN_POS[0];
  panner.positionY.value = SCREEN_POS[1];
  panner.positionZ.value = SCREEN_POS[2];
  const gain = ctx.createGain();
  gain.gain.value = 0;
  source.connect(panner).connect(gain).connect(ctx.destination);
  reel.audio = { ctx, gain, panner };
  window.__cinemaAudio = reel.audio;
}

export function CinemaScreen3D({ audioOn = true }) {
  const { camera } = useThree();
  const nearLight = useRef(null);
  const farLight = useRef(null);
  const st = useRef({ lum: 0.5, frame: 0, vol: 0, r: 0.8, g: 0.85, b: 1 });
  const reel = useMemo(getReel, []);
  const { video, texture } = reel;

  useEffect(() => {
    const kick = () => {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    };

    const onGesture = () => {
      // the audio graph can only be created/resumed from inside a real gesture
      reel.gestured = true;
      ensureAudio(reel);
      if (reel.audio && reel.audio.ctx.state !== "running") reel.audio.ctx.resume();
      kick();
    };

    kick();
    const onVisible = () => { if (!document.hidden) kick(); };
    // the projection must never stop: retry on any interaction / tab return
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("click", onGesture);
    window.addEventListener("keydown", onGesture);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => { if (video.paused) kick(); }, 2000);
    return () => {
      clearInterval(timer);
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("click", onGesture);
      window.removeEventListener("keydown", onGesture);
      document.removeEventListener("visibilitychange", onVisible);
      // leaving the studio: hush and freeze, but keep the single audio graph
      video.muted = true;
      video.volume = 0;
      if (reel.audio) {
        reel.audio.gain.gain.cancelScheduledValues(reel.audio.ctx.currentTime);
        reel.audio.gain.gain.value = 0;
        reel.audio.ctx.suspend();
      }
      video.pause();
    };
  }, [reel, video]);

  useFrame(({ clock }) => {
    const s = st.current;

    // sample the frame a few times per second (GPU readback: keep it cheap)
    if (video.readyState >= 2 && s.frame++ % 12 === 0) {
      try {
        reel.probeCtx.drawImage(video, 0, 0, 8, 8);
        const d = reel.probeCtx.getImageData(0, 0, 8, 8).data;
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
        const n = d.length / 4;
        r /= n * 255; g /= n * 255; b /= n * 255;
        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        s.lum += (lum - s.lum) * 0.35;
        const mx = Math.max(r, g, b) || 1;
        s.r = 0.45 + 0.55 * (r / mx);
        s.g = 0.45 + 0.55 * (g / mx);
        s.b = 0.5 + 0.5 * (b / mx);
      } catch (e) { /* frame not decodable yet */ }
    }

    // shutter + gate weave on top of the film's own brightness
    const t = clock.elapsedTime;
    const shutter = 0.9 + 0.1 * Math.sin(t * 47.5);
    const k = (0.1 + 1.55 * s.lum) * shutter;
    if (nearLight.current) {
      nearLight.current.intensity = 6 + 68 * k;
      nearLight.current.color.setRGB(s.r, s.g, s.b);
    }
    if (farLight.current) {
      farLight.current.intensity = 2 + 24 * k;
      farLight.current.color.setRGB(s.r, s.g, s.b);
    }

    // soundtrack: the listener follows the camera, the panner stays on the wall.
    // The level is driven on the media element itself (works in every browser,
    // even where WebAudio routing of a media element misbehaves) so outside the
    // auditorium the trailer is hard-muted, not just turned down.
    // The soundtrack exists only inside the auditorium (x 25 → 45): silent in
    // the soundstage and in the corridor, it fades in as you step through the
    // door and grows as you walk down to the screen.
    const inside =
      camera.position.x > 25 && camera.position.z > -13 && camera.position.z < 5;
    const door = THREE.MathUtils.clamp((camera.position.x - 25) / 2.5, 0, 1);
    const dist = Math.hypot(camera.position.x - SCREEN_POS[0], camera.position.z - SCREEN_POS[2]);
    const near = THREE.MathUtils.clamp((22 - dist) / 16, 0, 1);
    // hush at once while the studio page is animating out
    const onStudio = window.location.pathname === "/";
    const level =
      inside && onStudio && audioOn && reel.gestured ? door * (0.22 + 0.78 * near * near) : 0;

    if (level < 0.005) {
      if (!video.muted) { video.muted = true; video.volume = 0; s.vol = 0; }
    } else if (Math.abs(level - (s.vol || 0)) > 0.01) {
      s.vol = level;
      video.volume = level;
      if (video.muted) video.muted = false;
    }

    const a = reel.audio;
    if (a) {
      const l = a.ctx.listener;
      const dir = camera.getWorldDirection(FWD);
      if (l.positionX) {
        l.positionX.value = camera.position.x;
        l.positionY.value = camera.position.y;
        l.positionZ.value = camera.position.z;
        l.forwardX.value = dir.x; l.forwardY.value = dir.y; l.forwardZ.value = dir.z;
        l.upX.value = 0; l.upY.value = 1; l.upZ.value = 0;
      } else {
        l.setPosition(camera.position.x, camera.position.y, camera.position.z);
        l.setOrientation(dir.x, dir.y, dir.z, 0, 1, 0);
      }
      // the graph only adds the direction: the element volume does the fading
      a.gain.gain.setTargetAtTime(level > 0 ? 1 : 0, a.ctx.currentTime, 0.2);
    }
  });

  return (
    <group>
      <mesh position={SCREEN_POS} rotation={[0, -Math.PI / 2, 0]} data-testid="cinema-screen">
        <planeGeometry args={[SCREEN_W, SCREEN_H]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>
      {/* the picture lights the auditorium */}
      <pointLight ref={nearLight} position={[42.4, 3, -4]} intensity={60} distance={19} />
      <pointLight ref={farLight} position={[37.5, 3.2, -4]} intensity={18} distance={26} />
    </group>
  );
}
